import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { ProductCHF } from '@chancenwerk/_cms/products/entities/ProductCHF.js';
import '@chancenwerk/components/chancen-component-address-picker.js';
import '@chancenwerk/components/chancen-component-public-school-id-picker.js';
import { validateCustomerEmail, type LogistikOrderShippingDetails } from '@chancenwerk/db/LogistikOrder.js';
import { LogistikShippingRecipient } from '@chancenwerk/db/enums/LogistikEnums.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/cr-textarea.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { ChancenSchule } from '@chancenwerk/master-data-management/partner/entities/ChancenSchule.js';
import { Office } from '@chancenwerk/master-data-management/partner/entities/Office.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { School } from '@chancenwerk/master-data-management/partner/entities/School.js';
import { KPFPublicInformationAPI } from '@chancenwerk/public-access/api/KPFPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { DateTime } from 'luxon';
import { BehaviorSubject, of, switchMap, tap } from 'rxjs';
import { PartnerPublicInformationAPI } from '../../../public-access/api/PartnerPublicInformationAPI.js';
import './logistik-component-error-message.js';

/**
 * @customElement logistik-component-shipping-details
 */
@customElement('logistik-component-shipping-details')
export class LogistikComponentShippingDetails extends FormField<LogistikOrderShippingDetails> {
  @property({ type: Boolean, attribute: 'is-manually-created' }) isManuallyCreated?: boolean;
  standortNonOptional?: boolean;

  protected disabledFormFields: BehaviorSubject<BaseQLSelectionSet<LogistikOrderShippingDetails>> = new BehaviorSubject<
    BaseQLSelectionSet<LogistikOrderShippingDetails>
  >({});

  @state() readonly _form = new FormController(this, this.value.observable, {
    onSuccess: entity => this.onFormSuccess(entity),
    disabledFields: this.disabledFormFields,
  });
  @state() protected readonly _errorMsg = new RXController(this, '');
  @state() protected readonly _selectedSchool = new RXController<Maybe<Partner>>(this, null);
  @state() protected readonly _kpfsForSelectedSchool = new RXController(
    this,
    this._selectedSchool.observable,
    school$ =>
      school$.pipe(
        tap(() => {
          this._selectedKPF = undefined;
          this._errorMsg.next('');
        }),
        switchMap(school =>
          school
            ? KPFPublicInformationAPI.getBySchoolID([school._id])({
                kuerzel: 1,
                school: { kuerzel: 1 },
                produkt: { kuerzel: 1, name: 1 },
              }).then(kpfs => kpfs.filter(kpf => this._kpfFilter(kpf)))
            : of([]),
        ),
        tap(kpfs => {
          if (kpfs?.length === 1) this._selectedKPF = kpfs[0];
        }),
      ),
    { pipeIsLoading: true },
  );
  @state() private _selectedKPF?: KPF<any>;

  protected onFormSuccess(shippingDetails: LogistikOrderShippingDetails) {
    const errors = validateShippingDetails(shippingDetails, this.isManuallyCreated ?? false);

    if (errors.length) {
      if (errors.length === 1) throw errors[0];
      throw new MultipleValidationError(errors);
    }
    shippingDetails.kpfID = this._selectedKPF?._id;
  }

  override render() {
    return html`
      <x-flex space="md">
        <cr-input ${this._form.field('customerEmail')} placeholder="E-Mail"></cr-input>
        ${this._schoolPicker()} ${this._kpfPicker()}
        ${this._selectedSchool.value ? this._renderSchoolAddress() : nothing}
        ${this.value.value?.shippingRecipient === LogistikShippingRecipient.SCHUKO
          ? this._renderAddressInput()
          : nothing}
        <cr-date-picker-dropdown
          ${this._form.field('dueDate')}
          .isDateBlocked=${(date: DateTime) => {
            return date.diffNow('days').days < 6;
          }}
          placeholder="Frist"
          click-to-save
          @value-picked=${(e: ValueEvent<DateTime>) => {}}
        ></cr-date-picker-dropdown>
        <cr-textarea rows="5" ${this._form.field('shippingInstructions')} placeholder="Lieferanweisungen"></cr-textarea>
      </x-flex>
    `;
  }

  protected _schoolPicker() {
    return html`
      <chancen-component-public-school-id-picker
        ${this._form.field('shippingSchoolID')}
        placeholder="Standort"
        emptyState="Keine Standorte gefunden"
        @value-picked=${async (e: ValueEvent<string>) => {
          const schule = await PartnerPublicInformationAPI.getByID([e.detail.value])(Partner.allFields);
          if (schule.length === 1) {
            this._selectedSchool.next(schule[0] as Partner);
          }
        }}
        error=${ifDefined(this._errorMsg.value?.includes('Standort') ? this._errorMsg.value : undefined)}
        only-active
      ></chancen-component-public-school-id-picker>
    `;
  }

  private _renderAddressInput() {
    return html`
      <cr-input ${this._form.field('shippingRecipientName')} placeholder="Vollständiger Name"></cr-input>
      <cr-input ${this._form.field('shippingRecipientzuHaenden')} placeholder="z.H."></cr-input>
      <chancen-component-address-picker
        hide-all-countries
        ${this._form.field('shippingAddress')}
      ></chancen-component-address-picker>
    `;
  }

  private _renderSchoolAddress() {
    if (
      this._selectedSchool.value instanceof School ||
      this._selectedSchool.value instanceof ChancenSchule ||
      this._selectedSchool.value instanceof Office
    ) {
      return html`
        <cr-card ${css({ width: '100%' })}>
          <cr-flex space="sm">
            <cr-h3> Adresse </cr-h3>
            <cr-text>${this._selectedSchool.value.name}</cr-text>
            <cr-text> ${this._selectedSchool.value.address?.strasse} </cr-text>
            <cr-flex horizontal space="xs">
              <cr-text>${this._selectedSchool.value.address?.zipCity?.zip}</cr-text>
              <cr-text>${this._selectedSchool.value.address?.zipCity?.city}</cr-text>
            </cr-flex>
            <cr-checkbox
              label="Abweichende Lieferadresse?"
              @value-picked=${(e: ValueEvent<boolean>) => {
                if (!this.value.value) return;
                if (e.detail.value) {
                  this.value.value.shippingRecipient = LogistikShippingRecipient.SCHUKO;
                } else {
                  this.value.value.shippingRecipient = LogistikShippingRecipient.SCHOOL;
                }
                this.requestUpdate();
              }}
              .value=${this.value.value?.shippingRecipient === LogistikShippingRecipient.SCHUKO}
            ></cr-checkbox>
          </cr-flex>
          <cr-flex> </cr-flex>
        </cr-card>
      `;
    } else {
      return nothing;
    }
  }

  private _kpfPicker() {
    if (!this._selectedSchool.value) return nothing;
    if (this._kpfsForSelectedSchool.isLoading) return html` <cr-text> KPFs werden geladen... </cr-text> `;
    if (!this._kpfsForSelectedSchool.value?.length)
      return html` <cr-text> Es gibt keine KPFs für diesen Standort </cr-text> `;
    return html`
      <cr-dropdown-selection
        placeholder="Produkt${!this._selectedKPF
          ? ` auswählen`
          : this._kpfsForSelectedSchool.value.length === 1
          ? ' (nur ein Produkt vorhanden)'
          : ''}"
        .selectables=${this._kpfsForSelectedSchool.value}
        .renderString=${(item?: KPF<any>) => (item ? item.produkt?.name ?? '' : '')}
        .value=${this._selectedKPF}
        @value-picked=${(e: ValueEvent<KPF<any>>) => (this._selectedKPF = e.detail.value)}
        ?disabled=${this._kpfsForSelectedSchool.value.length === 1}
        error=${ifDefined(this._errorMsg.value?.includes('Produkt') ? this._errorMsg.value : undefined)}
      ></cr-dropdown-selection>
    `;
  }

  clearSelection() {
    this._selectedSchool.next(null);
  }

  /**
   * @param kpf KPF instance
   * @returns Whether to keep the given KPF in the list.
   */
  protected _kpfFilter(kpf: KPF<any>): boolean {
    if (this.isManuallyCreated) return true;
    return kpf.produkt?._class !== ProductCHF._class;
  }
}

export function validateShippingDetails(
  shippingDetails: LogistikOrderShippingDetails,
  isManuallyCreated: boolean,
): ValidationError[] {
  const errors: ValidationError[] = [];

  try {
    validateCustomerEmail(shippingDetails.customerEmail, isManuallyCreated, shippingDetails);
  } catch (e) {
    if (e instanceof ValidationError) errors.push(e);
    else throw e;
  }

  if (shippingDetails.shippingRecipient !== LogistikShippingRecipient.SCHOOL) {
    if (shippingDetails.shippingRecipientName === null || shippingDetails.shippingRecipientName === '') {
      errors.push(
        new ValidationError('Es muss ein Name angegeben werden.', {
          key: 'shippingRecipientName',
          target: shippingDetails,
        }),
      );
    }
    if (shippingDetails.shippingAddress?.strasse === null || shippingDetails.shippingAddress?.strasse === '') {
      errors.push(
        new ValidationError("'Es muss eine Straße ausgewählt werden.'", {
          path: ['shippingAddress'],
          key: 'strasse',
          target: shippingDetails,
        }),
      );
    }
    if (
      shippingDetails.shippingAddress?.zipCity?.zip === null ||
      shippingDetails.shippingAddress?.zipCity?.zip === ''
    ) {
      errors.push(
        new ValidationError('Es muss eine PLZ ausgewählt werden.', {
          path: ['shippingAddress', 'zipCity'],
          key: 'zip',
          target: shippingDetails,
        }),
      );
    }
  }

  return errors;
}
